/**************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2020 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ***************************************************************************/

const AEM_CIF_PRODUCT_PICKER = 'aem:cif:productpicker';
const AEM_CIF_CATEGORY_PICKER = 'aem:cif:categorypicker';
const AEM_CIF_PRODUCT_ADMIN = 'aem:cif:productadmin';
/**
 * Productfield component was loaded.
 */
export const event_productfield_loaded = {
    feature: 'aem:cif:productfield',
    element: 'productfield',
    action: 'loaded',
    type: 'productfield',
    widget: { name: 'productfield', type: 'productfield' }
};

/**
 * Productpicker component was loaded.
 */
export const event_productpicker_loaded = {
    feature: AEM_CIF_PRODUCT_PICKER,
    element: 'productpicker',
    action: 'loaded',
    type: 'productpicker',
    widget: { name: 'productpicker', type: 'productpicker' }
};

/**
 * Navigate the catalog hierarchy by clicking on categories or configureable products.
 */
export const event_productpicker_navigate = {
    feature: AEM_CIF_PRODUCT_PICKER,
    element: 'item',
    type: 'item',
    action: 'navigate',
    widget: { name: 'productpicker', type: 'productpicker' }
};

/**
 * Perform a search in the product picker.
 */
export const event_productpicker_search = {
    feature: AEM_CIF_PRODUCT_PICKER,
    element: 'searchbar',
    type: 'search',
    action: 'search',
    widget: { name: 'productpicker', type: 'productpicker' }
};

/**
 * Select an item (product or variant) in the product picker.
 */
export const event_productpicker_select = {
    feature: AEM_CIF_PRODUCT_PICKER,
    element: 'item',
    type: 'item',
    action: 'select',
    widget: { name: 'productpicker', type: 'productpicker' }
};

/**
 * Click on Cancel button in product picker.
 */
export const event_productpicker_cancel = {
    feature: AEM_CIF_PRODUCT_PICKER,
    element: 'productpicker',
    action: 'cancel',
    type: 'productpicker',
    widget: { name: 'productpicker', type: 'productpicker' }
};

/**
 * Click on Select button in product picker.
 */
export const event_productpicker_submit = {
    feature: AEM_CIF_PRODUCT_PICKER,
    element: 'productpicker',
    action: 'submit',
    type: 'productpicker',
    widget: { name: 'productpicker', type: 'productpicker' }
};

/**
 * Submit of stagedcatalog field in product admin.
 */
export const event_productpicker_stagedcatalog = {
    feature: AEM_CIF_PRODUCT_PICKER,
    element: 'stagedcatalog',
    type: 'date',
    action: 'submit',
    widget: { name: 'productpicker', type: 'productpicker' }
};

/**
 * Categoryfield component was loaded.
 */
export const event_categoryfield_loaded = {
    feature: 'aem:cif:categoryfield',
    element: 'categoryfield',
    action: 'loaded',
    type: 'categoryfield',
    widget: { name: 'categoryfield', type: 'categoryfield' }
};

/**
 * Categorypicker component was loaded.
 */
export const event_categorypicker_loaded = {
    feature: AEM_CIF_CATEGORY_PICKER,
    element: 'categorypicker',
    action: 'loaded',
    type: 'categorypicker',
    widget: { name: 'categorypicker', type: 'categorypicker' }
};

/**
 * Navigate the catalog hierarchy by clicking on categories.
 */
export const event_categorypicker_navigate = {
    feature: AEM_CIF_CATEGORY_PICKER,
    element: 'item',
    action: 'navigate',
    type: 'item',
    widget: { name: 'categorypicker', type: 'categorypicker' }
};

/**
 * Perform a search in the category picker.
 */
export const event_categorypicker_search = {
    feature: AEM_CIF_CATEGORY_PICKER,
    element: 'searchbar',
    type: 'search',
    action: 'search',
    widget: { name: 'categorypicker', type: 'categorypicker' }
};

/**
 * Select an item (category) in the category picker.
 */
export const event_categorypicker_select = {
    feature: AEM_CIF_CATEGORY_PICKER,
    element: 'item',
    action: 'select',
    type: 'item',
    widget: { name: 'categorypicker', type: 'categorypicker' }
};

/**
 * Click on Cancel button in category picker.
 */
export const event_categorypicker_cancel = {
    feature: AEM_CIF_CATEGORY_PICKER,
    element: 'categorypicker',
    action: 'cancel',
    type: 'categorypicker',
    widget: { name: 'categorypicker', type: 'categorypicker' }
};

/**
 * Click on Select button in category picker.
 */
export const event_categorypicker_submit = {
    feature: AEM_CIF_CATEGORY_PICKER,
    element: 'categorypicker',
    action: 'submit',
    type: 'categorypicker',
    widget: { name: 'categorypicker', type: 'categorypicker' }
};

/**
 * Click on category card in product admin.
 */
export const event_productadmin_categorycard = {
    feature: AEM_CIF_PRODUCT_ADMIN,
    element: 'categorycard',
    type: 'item',
    action: 'click',
    widget: { name: 'productadmin', type: 'productadmin' }
};

/**
 * Click on product card in product admin.
 */
export const event_productadmin_productcard = {
    feature: AEM_CIF_PRODUCT_ADMIN,
    element: 'productcard',
    type: 'item',
    action: 'click',
    widget: { name: 'productadmin', type: 'productadmin' }
};

/**
 * Click on config card in product admin.
 */
export const event_productadmin_configcard = {
    feature: AEM_CIF_PRODUCT_ADMIN,
    element: 'configcard',
    type: 'item',
    action: 'click',
    widget: { name: 'productadmin', type: 'productadmin' }
};

/**
 * Click on properties action on category card.
 */
export const event_productadmin_categoryproperties = {
    feature: AEM_CIF_PRODUCT_ADMIN,
    element: 'properties',
    type: 'button',
    action: 'click',
    widget: { name: 'categorycard', type: 'categorycard' }
};

/**
 * Click on properties action on product card.
 */
export const event_productadmin_productproperties = {
    feature: AEM_CIF_PRODUCT_ADMIN,
    element: 'properties',
    type: 'button',
    action: 'click',
    widget: { name: 'productcard', type: 'productcard' }
};

/**
 * Click on properties action on config card.
 */
export const event_productadmin_configproperties = {
    feature: AEM_CIF_PRODUCT_ADMIN,
    element: 'properties',
    type: 'button',
    action: 'click',
    widget: { name: 'configcard', type: 'configcard' }
};

/**
 * Submit of search field in product admin.
 */
export const event_productadmin_search = {
    feature: AEM_CIF_PRODUCT_ADMIN,
    element: 'searchbar',
    type: 'search',
    action: 'submit',
    widget: { name: 'productadmin', type: 'productadmin' }
};

/**
 * Submit of stagedcatalog field in product admin.
 */
export const event_productadmin_stagedcatalog = {
    feature: AEM_CIF_PRODUCT_ADMIN,
    element: 'stagedcatalog',
    type: 'date',
    action: 'submit',
    widget: { name: 'productadmin', type: 'productadmin' }
};

/**
 * Select field in sort dropdown in product admin.
 */
export const event_productadmin_sortfield = {
    feature: AEM_CIF_PRODUCT_ADMIN,
    element: 'sortfield',
    type: 'dropdown',
    action: 'select',
    widget: { name: 'productadmin', type: 'productadmin' }
};

/**
 * Click on sort direction button in product admin.
 */
export const event_productadmin_sortdirection = {
    feature: AEM_CIF_PRODUCT_ADMIN,
    element: 'sortdirection',
    type: 'button',
    action: 'click',
    widget: { name: 'productadmin', type: 'productadmin' }
};

/**
 * Click on close button in product properties.
 */
export const event_productproperties_close = {
    feature: AEM_CIF_PRODUCT_ADMIN,
    element: 'close',
    type: 'button',
    action: 'click',
    widget: { name: 'productproperties', type: 'productproperties' }
};

/**
 * Click on close button in category properties.
 */
export const event_categoryproperties_close = {
    feature: AEM_CIF_PRODUCT_ADMIN,
    element: 'close',
    type: 'button',
    action: 'click',
    widget: { name: 'categoryproperties', type: 'categoryproperties' }
};
